// Open-a-DM / start-a-DM-call launchers usable from ANY surface (People
// birthdays list, rosters, profile pages) — not just the messenger UI.
//
// Both reuse the messenger's canonical flows:
//   - "write": dmStore.createDmChannelOptimistic → navigate to the chat
//     (same Client-First pattern as TelegramChatList's contact click; the
//     server's POST /dm-channels dedupes an existing 1:1).
//   - "call": same store mutation as TelegramChatView.initiateCall —
//     setCallState('outgoing') + `dm:call:invite` socket emit. The call
//     overlay renders inside the DM view, so we navigate there first; the
//     accept/decline listeners are global (ToastContainer/App).
import type { NavigateFunction } from 'react-router-dom'
import { useDmStore } from '@os/messenger/web/stores/dmStore'
import { useDmCallStore } from '@os/messenger/web/stores/dmCallStore'
import { useAuthStore } from '../stores/authStore'
import { getSocket } from './socket'
import type { DMChannel, User } from '../types'

export interface DmLaunchTarget {
  id: string
  username: string
  avatarUrl?: string | null
  status?: User['status'] | null
}

/** Existing non-temp 1:1 DM with `userId` from the already-loaded list, if any. */
function findDirectDm(userId: string): DMChannel | undefined {
  const meId = useAuthStore.getState().user?.id
  if (!meId) return undefined
  return useDmStore.getState().dmChannels.find(
    (c) =>
      c.type === 'PRIVATE' &&
      !c.id.startsWith('temp-') &&
      c.members?.length === 2 &&
      c.members.some((m) => m.userId === userId) &&
      c.members.some((m) => m.userId === meId),
  )
}

/**
 * Ensure a 1:1 DM with `target` exists and navigate to it. `onReady` fires
 * with the canonical (non-temp) DM id once known — but only while the user
 * is still on that chat's URL, so a mid-flight navigation away cancels it.
 */
function ensureDmAndGo(
  navigate: NavigateFunction,
  target: DmLaunchTarget,
  onReady?: (dmId: string) => void,
): void {
  const currentUser = useAuthStore.getState().user
  if (!currentUser || currentUser.id === target.id) return

  const existing = findDirectDm(target.id)
  if (existing) {
    navigate(`/channels/dm-${existing.id}`)
    onReady?.(existing.id)
    return
  }

  const { tempId, promise } = useDmStore.getState().createDmChannelOptimistic(
    {
      id: target.id,
      username: target.username,
      avatarUrl: target.avatarUrl ?? null,
      status: target.status ?? 'OFFLINE',
    },
    currentUser,
  )
  navigate(`/channels/dm-${tempId}`)
  promise
    .then((dm) => {
      // Same still-on-the-temp-URL guard as TelegramChatList: if the user
      // navigated elsewhere during the round-trip, leave them be.
      if (window.location.pathname !== `/channels/dm-${tempId}`) return
      navigate(`/channels/dm-${dm.id}`, { replace: true })
      onReady?.(dm.id)
    })
    .catch((err) => {
      console.error('[dmLaunch] failed to create DM:', err)
      if (window.location.pathname === `/channels/dm-${tempId}`) {
        navigate('/channels/@me', { replace: true })
      }
    })
}

/** "Write a message" — open (creating if needed) the 1:1 DM with `target`. */
export function openDmWithUser(navigate: NavigateFunction, target: DmLaunchTarget): void {
  ensureDmAndGo(navigate, target)
}

/** "Call" — open the 1:1 DM with `target` and start an outgoing audio call. */
export function callDmUser(navigate: NavigateFunction, target: DmLaunchTarget): void {
  const currentUser = useAuthStore.getState().user
  if (!currentUser || currentUser.id === target.id) return

  // Already in a DM call (any state) — just open the chat instead of
  // stacking a second outgoing invite on top of the live one.
  if (useDmCallStore.getState().callState !== 'idle') {
    ensureDmAndGo(navigate, target)
    return
  }

  ensureDmAndGo(navigate, target, (dmId) => {
    // Mirror TelegramChatView.initiateCall exactly: outgoing state shows
    // the callee's name; the socket payload carries the caller's.
    useDmCallStore.getState().setCallState('outgoing', {
      callType: 'audio',
      dmId,
      callerName: target.username,
    })
    getSocket().emit('dm:call:invite', {
      dmChannelId: dmId,
      callerId: currentUser.id,
      callerName: currentUser.username,
      callType: 'audio',
    })
  })
}
